import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS_RANGES = ['< 500', '500 – 1 000', '1 000 – 5 000', '> 5 000'];
const PLANS = ['Start', 'Pro', 'Full pakke'];

export default function WaitlistModal({ open, onClose, defaultPlan = 'Pro' }) {
  const [form, setForm] = useState({
    name: '', email: '', shop_name: '', phone: '', shop_url: '',
    products_count: '', interested_plan: defaultPlan, message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(f => ({ ...f, interested_plan: defaultPlan }));
      setDone(false);
      setError('');
    }
  }, [open, defaultPlan]);

  // Lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.shop_name.trim()) {
      setError('Fyll inn navn, e-post og butikknavn.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        name: form.name.trim(),
        email: form.email.trim(),
        shop_name: form.shop_name.trim(),
        phone: form.phone.trim() || null,
        shop_url: form.shop_url.trim() || null,
        products_count: form.products_count || null,
        interested_plan: form.interested_plan || null,
        message: form.message.trim() || null,
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Noe gikk galt – prøv igjen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm"
      data-testid="waitlist-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xl bg-card border border-border rounded-2xl shadow-[0_24px_64px_rgba(15,23,42,0.18)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors duration-200 z-10"
          data-testid="waitlist-modal-close"
          aria-label="Lukk"
        >
          <X size={18} strokeWidth={1.75} />
        </button>

        {done ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-success/10 text-success flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={28} strokeWidth={1.75} />
            </div>
            <h3 className="text-2xl font-semibold tracking-tight mb-2">Du er på lista! 🎉</h3>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
              Takk! Vi tar kontakt på <span className="font-medium text-foreground">{form.email}</span> så
              snart det er ledig plass i closed beta. Tidlige medlemmer får lifetime-rabatt.
            </p>
            <button
              onClick={onClose}
              className="mt-7 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-foreground text-background hover:bg-foreground/90 transition-colors duration-200 ease-out active:scale-[0.98]"
              data-testid="waitlist-modal-done-button"
            >
              Lukk
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 lg:p-8">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-primary mb-2">
              Venteliste
            </div>
            <h3 className="text-2xl font-semibold tracking-tight mb-1">Bli med i closed beta</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Vi slipper inn 10 butikker av gangen for å sikre kvaliteten på oppsett og support.
              Tidlige medlemmer får lifetime-rabatt.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Navn *">
                <input type="text" required value={form.name} onChange={update('name')}
                  className={inputCls} data-testid="waitlist-name" placeholder="Ola Nordmann" />
              </Field>
              <Field label="E-post *">
                <input type="email" required value={form.email} onChange={update('email')}
                  className={inputCls} data-testid="waitlist-email" placeholder="ola@minbutikk.no" />
              </Field>
              <Field label="Butikknavn *">
                <input type="text" required value={form.shop_name} onChange={update('shop_name')}
                  className={inputCls} data-testid="waitlist-shop" placeholder="Min Butikk AS" />
              </Field>
              <Field label="Telefon">
                <input type="tel" value={form.phone} onChange={update('phone')}
                  className={inputCls} data-testid="waitlist-phone" placeholder="+47 ..." />
              </Field>
              <Field label="Butikk-URL" className="sm:col-span-2">
                <input type="url" value={form.shop_url} onChange={update('shop_url')}
                  className={inputCls} data-testid="waitlist-url" placeholder="https://minbutikk.no" />
              </Field>
              <Field label="Antall varer">
                <select value={form.products_count} onChange={update('products_count')}
                  className={inputCls} data-testid="waitlist-products">
                  <option value="">Velg…</option>
                  {PRODUCTS_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Plan">
                <select value={form.interested_plan} onChange={update('interested_plan')}
                  className={inputCls} data-testid="waitlist-plan">
                  {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Fortell kort (valgfritt)" className="sm:col-span-2">
                <textarea rows={3} value={form.message} onChange={update('message')}
                  className={`${inputCls} resize-none`} data-testid="waitlist-message"
                  placeholder="Fortell kort om butikken og hva du trenger…" />
              </Field>
            </div>

            {error && (
              <div className="mt-4 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-destructive" data-testid="waitlist-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-medium
                bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50
                transition-colors duration-200 ease-out active:scale-[0.98]
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              data-testid="waitlist-submit"
            >
              {submitting ? (<><Loader2 size={15} className="animate-spin" /> Sender…</>) : (<>Meld meg på <ArrowRight size={15} strokeWidth={2} /></>)}
            </button>

            <p className="mt-3 text-[11px] text-muted-foreground text-center">
              Vi bruker informasjonen kun for å kontakte deg om RetailMaster. Ingen spam.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-shadow duration-200";

function Field({ label, className = '', children }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="text-xs font-medium text-foreground">{label}</span>
      {children}
    </label>
  );
}
